import * as React from 'react';
import { Alert, Link } from 'omakase';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { makeStyles } from '@material-ui/core/styles';
import HideFromIOSNativeApp from 'components/ui/HideFromIOSNativeApp';

export type DoubleBooking =
  | DoubleBookingNone
  | DoubleBookingSameRestaurant
  | DoubleBookingOtherRestaurant
  | DoubleBookingMultiple
  | DoubleBookingSameRestaurantConcierge;

interface DoubleBookingNone {
  type: 'DOUBLE_BOOKING_NONE';
  restrictBooking?: boolean;
}

interface DoubleBookingSameRestaurantConcierge {
  type: 'DOUBLE_BOOKING_SAME_RESTAURANT_CONCIERGE';
  restaurantName?: string;
  restrictBooking?: boolean;
}

interface DoubleBookingSameRestaurant {
  type: 'DOUBLE_BOOKING_SAME_RESTAURANT';
  restaurantName?: string;
  restrictBooking?: boolean;
}

interface DoubleBookingOtherRestaurant {
  type: 'DOUBLE_BOOKING_OTHER_RESTAURANT';
  restrictBooking?: boolean;
}

interface DoubleBookingMultiple {
  type: 'DOUBLE_BOOKING_MULTIPLE';
  restrictBooking?: boolean;
}

interface Props {
  doubleBooking: DoubleBooking;
  currentRestaurantName?: string;
}

const DoubleBookingWarning: React.FunctionComponent<Props> = ({
  doubleBooking,
  currentRestaurantName,
}) => {
  let message: React.ReactNode;
  let includeSeeReservationsAction = true;
  if (doubleBooking.restrictBooking) {
    switch (doubleBooking.type) {
      case 'DOUBLE_BOOKING_NONE': {
        return null;
      }
      case 'DOUBLE_BOOKING_SAME_RESTAURANT_CONCIERGE':
        message = (
          <>
            This business does not allow new bookings within an hour of an existing booking for the
            same guest. Please consult with the guest or choose a new time that doesn't conflict
            with their existing reservation.
          </>
        );
        includeSeeReservationsAction = false;
        break;
      case 'DOUBLE_BOOKING_SAME_RESTAURANT':
      case 'DOUBLE_BOOKING_OTHER_RESTAURANT':
      case 'DOUBLE_BOOKING_MULTIPLE':
        message = (
          <>
            You can't make this reservation{' '}
            {currentRestaurantName ? `at ${currentRestaurantName} ` : ''}
            because you already have another reservation within an hour of this time. Please choose
            a new time that doesn't conflict with your existing reservation.
          </>
        );
        break;
      default:
        return assertNever(doubleBooking);
    }
  } else {
    switch (doubleBooking.type) {
      case 'DOUBLE_BOOKING_NONE': {
        return null;
      }
      case 'DOUBLE_BOOKING_SAME_RESTAURANT':
        message = (
          <>
            You already have another reservation around this time at{' '}
            {doubleBooking.restaurantName || 'this location'}.
          </>
        );
        break;
      case 'DOUBLE_BOOKING_SAME_RESTAURANT_CONCIERGE':
        message = (
          <>
            The guest you are booking for already has another reservation around this time at{' '}
            {doubleBooking.restaurantName || 'this location'}.
          </>
        );
        includeSeeReservationsAction = false;
        break;
      case 'DOUBLE_BOOKING_OTHER_RESTAURANT':
        message = 'You already have another reservation around this time at another location.';
        break;
      case 'DOUBLE_BOOKING_MULTIPLE':
        message = 'You already have other reservations around this time.';
        break;
      default:
        return assertNever(doubleBooking);
    }
  }
  return (
    <DoubleBookingAlert includeSeeReservationAction={includeSeeReservationsAction}>
      {message}
    </DoubleBookingAlert>
  );
};

const useDoubleBookingAlertStyles = makeStyles(
  (theme) => ({
    root: {
      marginBottom: theme.spacing(2),
    },
  }),
  { index: 1 }
);

const DoubleBookingAlert: React.FunctionComponent<{ includeSeeReservationAction: boolean }> = ({
  includeSeeReservationAction,
  children,
}) => {
  const classes = useDoubleBookingAlertStyles();
  return (
    <Alert
      className={classes.root}
      severity="info"
      action={
        includeSeeReservationAction ? (
          <HideFromIOSNativeApp>
            <Link target="_blank" href="/profile/reservations">
              See your reservations
            </Link>
          </HideFromIOSNativeApp>
        ) : undefined
      }
    >
      {children}
    </Alert>
  );
};

export default DoubleBookingWarning;
